import React, {useCallback, useEffect, useRef} from 'react';
import {
    Box,
    IconButton,
    Paper,
    Tab,
    Tabs,
    Tooltip,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChatWindow from './ChatWindow';
import TeamTab from './TeamTab';
import useUserChats from '../hooks/useUserChats';
import useChartTeamContext from '../hooks/useChartTeamContext';
import {useSession} from '../firebase/UserProvider';
import useChatStore from '../stores/useChatStore';
import useAiWidgetStore from '../stores/useAiWidgetStore';
import {useStompSubscription} from '../hooks/useStompSubscription';
import {playDing, primeAudio} from '../utils/playDing';
import GlobalAiAssistantPanel from '../features/aiAssistantChat/GlobalAiAssistantPanel';

function ParentScopedChatPaper({chat, rightOffset, onClose}) {
    const rootRef = useRef(null);
    const armedRef = useRef(false);

    useEffect(() => {
        if (!chat?.parentId) return undefined;
        armedRef.current = false;
        const arm = window.setTimeout(() => {
            armedRef.current = true;
        }, 0);
        const onPointerDown = (event) => {
            if (!armedRef.current) return;
            const root = rootRef.current;
            if (root && (root === event.target || root.contains(event.target))) return;
            onClose();
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => {
            window.clearTimeout(arm);
            document.removeEventListener('pointerdown', onPointerDown, true);
        };
    }, [chat?.parentId, chat?.id, onClose]);

    return (
        <Paper
            ref={rootRef}
            data-chat-window=""
            data-chat-id={chat.id}
            elevation={6}
            sx={{
                position: 'fixed',
                top: '50%',
                transform: 'translateY(-50%)',
                right: rightOffset,
                width: CHAT_WINDOW_WIDTH,
                height: 'min(728px, calc(100vh - 120px))',
                zIndex: 1401,
                borderRadius: '12px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <ChatWindow chat={chat} onClose={onClose}/>
        </Paper>
    );
}

const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 900;
const PANEL_WIDTH = 448;
const CHAT_WINDOW_WIDTH = 420;
const GAP = 12;
const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

/**
 * Unified right-side communication panel.
 *
 * Tabs:
 *   AI    — GlobalAiAssistantPanel (context-aware AI assistant)
 *   Team  — 1:1 inbox + chart/product entity group threads
 */
export default function CommunicationHub() {
    const activeTab = useAiWidgetStore((s) => s.activeTab);
    const setActiveTab = useAiWidgetStore((s) => s.setActiveTab);
    const open = useAiWidgetStore((s) => s.open);
    const setOpen = useAiWidgetStore((s) => s.setOpen);
    const panelWidth = useAiWidgetStore((s) => s.panelWidth);
    const setPanelWidth = useAiWidgetStore((s) => s.setPanelWidth);
    const tickPanelResizeEnd = useAiWidgetStore((s) => s.tickPanelResizeEnd);

    const isResizing = useRef(false);

    const startResize = useCallback((e) => {
        e.preventDefault();
        isResizing.current = true;
        const startX = e.clientX;
        const startW = panelWidth;

        const onMove = (ev) => {
            if (!isResizing.current) return;
            const delta = startX - ev.clientX; // dragging left → bigger panel
            setPanelWidth(startW + delta);
        };
        const onUp = () => {
            isResizing.current = false;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            tickPanelResizeEnd();
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [panelWidth, setPanelWidth]);

    useChartTeamContext();

    const startNewAiChatRef = React.useRef(null);

    // Tick when the panel opens or closes so the whiteboard can zoom-to-fit.
    const prevOpenRef = React.useRef(open);
    React.useEffect(() => {
        if (prevOpenRef.current !== open) {
            prevOpenRef.current = open;
            tickPanelResizeEnd();
        }
    }, [open, tickPanelResizeEnd]);

    const {chats, loading, error, refetch} = useUserChats({limit: 50});

    const {user: currentUser} = useSession() || {};
    const uid = currentUser?.uid || currentUser?.id || null;

    useEffect(() => {
        useChatStore.getState().init();
        return () => useChatStore.getState().cleanup();
    }, []);

    useEffect(() => {
        const prime = () => primeAudio();
        window.addEventListener('click', prime, {once: true});
        window.addEventListener('keydown', prime, {once: true});
        return () => {
            window.removeEventListener('click', prime);
            window.removeEventListener('keydown', prime);
        };
    }, []);

    const handleIncomingNotification = useCallback(async (notification) => {
        const chatId = notification?.chatId;
        const event = notification?.event;
        if (!chatId) return;

        if (event === 'chat_deleted') {
            useChatStore.getState().removeChat(chatId);
            return;
        }

        if (notification?.silent) return;

        try {
            const token = localStorage.getItem('access_token');
            const headers = token ? {Authorization: `Bearer ${token}`} : {};
            const resp = await fetch(`${BASE}/api/chats/${encodeURIComponent(chatId)}`, {headers});
            const chat = resp.ok ? await resp.json() : {id: chatId};

            const isFintelChat =
                chat.chatType === 'fintel' ||
                (Array.isArray(chat.participants) && chat.participants.length === 1);
            if (isFintelChat) return;

            playDing();
            if (chat && chat.id) useChatStore.getState().addChat(chat);
        } catch {
            // ignore
        }
    }, [BASE]);

    useStompSubscription(
        uid ? `/topic/chat/notify/${uid}` : null,
        handleIncomingNotification,
    );

    const activeChats = useChatStore((s) => s.activeChats);
    const removeChat = useChatStore((s) => s.removeChat);

    const handleTabChange = (_e, newTab) => setActiveTab(newTab);

    // /chat routes are decommissioned; CommunicationHub is now always rendered

    return (
        <>
            {!open && (
                <Box
                    onClick={() => setOpen(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setOpen(true);
                    }}
                    sx={{
                        position: 'fixed',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1400,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        width: 36,
                        py: 1.75,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '8px 0 0 8px',
                        cursor: 'pointer',
                        boxShadow: 4,
                        transition: 'background-color 150ms ease',
                        '&:hover': {bgcolor: 'primary.dark'},
                    }}
                    aria-label="Open communication hub"
                >
                    <SmartToyOutlinedIcon fontSize="small"/>
                    <PeopleAltOutlinedIcon fontSize="small" sx={{opacity: 0.7}}/>
                </Box>
            )}

            <Paper
                sx={{
                    position: 'fixed',
                    right: 0,
                    top: 0,
                    transform: open ? 'translateX(0)' : 'translateX(110%)',
                    transition: 'transform 220ms ease',
                    zIndex: 1400,
                    pointerEvents: open ? 'auto' : 'none',
                    width: panelWidth,
                    height: '100vh',
                    borderRadius: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    boxShadow: (theme) => theme.shadows[6],
                }}
                role="region"
                aria-label="Communication hub"
            >
                {/* left-edge drag handle */}
                <Box
                    onMouseDown={startResize}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: 5,
                        height: '100%',
                        cursor: 'col-resize',
                        zIndex: 1,
                        '&:hover': {bgcolor: 'primary.main', opacity: 0.25},
                        '&:active': {bgcolor: 'primary.main', opacity: 0.4},
                        transition: 'background-color 150ms ease, opacity 150ms ease',
                    }}
                />
                <Box sx={{display: 'flex', alignItems: 'center', flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider'}}>
                    <Tabs
                        value={activeTab === 'chart' ? 'team' : activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{flex: 1}}
                    >
                        <Tab
                            value="ai"
                            label="Minsky Assistant"
                            icon={<SmartToyOutlinedIcon fontSize="small"/>}
                            iconPosition="start"
                        />
                        <Tab
                            value="team"
                            label="Team"
                            icon={<PeopleAltOutlinedIcon fontSize="small"/>}
                            iconPosition="start"
                        />
                    </Tabs>
                    <Tooltip title="Close" placement="left" enterDelay={600} enterNextDelay={600}>
                        <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close panel" sx={{mr: 0.5}}>
                            <ChevronRightIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Box>

                {activeTab === 'ai' && (
                    <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                        <GlobalAiAssistantPanel isActive={open} startNewConversationRef={startNewAiChatRef}/>
                    </Box>
                )}

                {(activeTab === 'team' || activeTab === 'chart') && (
                    <Box sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                        <TeamTab chats={chats} loading={loading} error={error} refetch={refetch}/>
                    </Box>
                )}
            </Paper>

            {activeChats.map((c, i) => {
                const rightOffset = panelWidth + GAP + i * (CHAT_WINDOW_WIDTH + GAP);
                return (
                    <ParentScopedChatPaper
                        key={c.id}
                        chat={c}
                        rightOffset={rightOffset}
                        onClose={() => removeChat(c.id)}
                    />
                );
            })}
        </>
    );
}
